import { SUPABASE_URL } from '../utils/supabase/url';
/**
 * Shared Inbox client — backs the Group Inbox view and email agent admin UI.
 *
 * Mirrors the shape returned by the server routes in
 * shared_inbox_routes.tsx and email_agent_routes.tsx. workspaceId is
 * required on every call so cross-workspace reads are blocked at the server.
 */
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

export type {
  SharedInboxStatus, SharedInboxThreadGroup, SharedInboxLatestMessagePreview, SharedInboxThreadSummary,
  SharedInboxUserRef, SharedInboxThreadDetail, SharedInboxMessage, SharedInboxAttachment,
} from './sharedInboxTypes';
import type { SharedInboxStatus, SharedInboxThreadSummary, SharedInboxThreadDetail, SharedInboxMessage } from './sharedInboxTypes';

// ── Email agent types ─────────────────────────────────────────────────────────

export interface SharedEmailDraft {
  id: string;
  threadId: string;
  workspaceId: string;
  bodyText: string;
  bodyHtml: string | null;
  generatedBy: string;
  assignedReviewerUserId: string | null;
  status: 'pending_review' | 'sent' | 'discarded';
  sentAt: string | null;
  discardedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailAgentConfig {
  id?: string;
  workspaceId: string | null;
  agentEnabled: boolean;
  maxThreadsPerRun: number;
  confidenceThreshold: number;
}

async function readError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    return (data && (data.error || data.message)) || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

export class SharedInboxService {
  async listThreads(input: {
    workspaceId: string;
    groupId?: string;
    status?: SharedInboxStatus;
    limit?: number;
  }): Promise<SharedInboxThreadSummary[]> {
    const params = new URLSearchParams({ workspaceId: input.workspaceId });
    if (input.groupId) params.set('groupId', input.groupId);
    if (input.status) params.set('status', input.status);
    if (input.limit) params.set('limit', String(input.limit));
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/threads?${params}`, { headers: hdrs });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    return Array.isArray(data?.threads) ? data.threads : [];
  }

  /** Saves an attachment ingest kept, under its own name, so the download bar opens it in the computer's own app. */
  async downloadAttachment(input: { workspaceId: string; threadId: string; attachmentId: string; filename: string }): Promise<void> {
    const params = new URLSearchParams({ workspaceId: input.workspaceId });
    const response = await fetch(
      `${API_BASE}/shared-inbox/threads/${encodeURIComponent(input.threadId)}/attachments/${encodeURIComponent(input.attachmentId)}?${params}`,
      { headers: await getAuthHeaders() },
    );
    if (!response.ok) throw new Error(await readError(response));
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = input.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked later: some browsers are still reading the blob when click() returns.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  async getThread(threadId: string, workspaceId: string): Promise<{ thread: SharedInboxThreadDetail; messages: SharedInboxMessage[] }> {
    const params = new URLSearchParams({ workspaceId });
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(
      `${API_BASE}/shared-inbox/threads/${encodeURIComponent(threadId)}?${params}`,
      { headers: hdrs },
    );
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }

  // input.userId is accepted for existing callers but never sent — the
  // server derives identity from the bearer token and would 403 on any
  // supplied value that doesn't match it, so forwarding a possibly-stale
  // local value only adds a way to fail.
  async setStatus(threadId: string, input: { workspaceId: string; userId: string; status: SharedInboxStatus }): Promise<SharedInboxThreadDetail> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/threads/${encodeURIComponent(threadId)}/status`, {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({ workspaceId: input.workspaceId, status: input.status }),
    });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    return data.thread;
  }

  async claim(threadId: string, input: { workspaceId: string; userId: string; unclaim?: boolean }): Promise<SharedInboxThreadDetail> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/threads/${encodeURIComponent(threadId)}/claim`, {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({ workspaceId: input.workspaceId, unclaim: input.unclaim }),
    });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    return data.thread;
  }

  /** Backfill a registered group's history by searching the calling user's
   * Gmail. Idempotent — re-runs and other team members' runs converge on
   * the same shared rows via the (group_id, internet_message_id) dedup. */
  async backfill(input: {
    workspaceId: string;
    groupId: string;
    userId: string;
    days?: number;
    limit?: number;
    commandId?: string;
    afterCommandId?: string;
  }): Promise<{ scanned: number; routed: number; alreadyMirrored: number; pages: number; address: string; commandId?:string; nextCursor?:string|null; complete?:boolean; unknown?:number; failed?:number; incompleteAttachments?:number; windowUnresolved?:number; start?:string; end?:string }> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/backfill`, {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({
        workspaceId: input.workspaceId,
        groupId: input.groupId,
        days: input.days,
        limit: input.limit,
        commandId: input.commandId,
        afterCommandId: input.afterCommandId,
      }),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }

  /** Read state is the caller's own; it never changes the thread for the team. */
  async markRead(threadId: string, workspaceId: string, unread = false): Promise<{ threadId: string; readAt: string | null }> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/threads/${encodeURIComponent(threadId)}/read`, {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({ workspaceId, unread }),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }

  async unreadCounts(workspaceId: string, groupIds: string[]): Promise<Record<string, number>> {
    if (groupIds.length === 0) return {};
    const params = new URLSearchParams({ workspaceId, groupIds: groupIds.join(',') });
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/unread-counts?${params}`, { headers: hdrs });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    return data?.counts && typeof data.counts === 'object' ? data.counts : {};
  }

  // ── Draft methods ─────────────────────────────────────────────────────────

  async getDrafts(threadId: string, workspaceId: string): Promise<SharedEmailDraft[]> {
    const params = new URLSearchParams({ threadId, workspaceId });
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/drafts?${params}`, { headers: hdrs });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    return Array.isArray(data?.drafts) ? data.drafts : [];
  }

  async updateDraftStatus(draftId: string, status: 'sent' | 'discarded', workspaceId: string): Promise<SharedEmailDraft> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/drafts/${encodeURIComponent(draftId)}`, {
      method: 'PATCH',
      headers: hdrs,
      body: JSON.stringify({ status, workspaceId }),
    });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    return data.draft;
  }

  // ── Email agent config methods ─────────────────────────────────────────────

  async getEmailAgentConfig(workspaceId: string): Promise<EmailAgentConfig> {
    const params = new URLSearchParams({ workspaceId });
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/email-agent/config?${params}`, { headers: hdrs });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    // Server returns snake_case from DB — normalise to camelCase here.
    const cfg = data.config ?? {};
    return {
      id: cfg.id,
      workspaceId: cfg.workspace_id ?? null,
      agentEnabled: cfg.agent_enabled ?? true,
      maxThreadsPerRun: cfg.max_threads_per_run ?? 25,
      confidenceThreshold: cfg.confidence_threshold ?? 0.70,
    };
  }

  async updateEmailAgentConfig(
    workspaceId: string,
    patch: Partial<Pick<EmailAgentConfig, 'agentEnabled' | 'maxThreadsPerRun' | 'confidenceThreshold'>>,
  ): Promise<EmailAgentConfig> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/email-agent/config`, {
      method: 'PATCH',
      headers: hdrs,
      body: JSON.stringify({
        workspaceId,
        agent_enabled: patch.agentEnabled,
        max_threads_per_run: patch.maxThreadsPerRun,
        confidence_threshold: patch.confidenceThreshold,
      }),
    });
    if (!response.ok) throw new Error(await readError(response));
    const data = await response.json();
    const cfg = data.config ?? {};
    return {
      id: cfg.id,
      workspaceId: cfg.workspace_id ?? null,
      agentEnabled: cfg.agent_enabled ?? true,
      maxThreadsPerRun: cfg.max_threads_per_run ?? 25,
      confidenceThreshold: cfg.confidence_threshold ?? 0.70,
    };
  }

  async runEmailAgentBacklog(workspaceId: string): Promise<{ processed: number; skipped: number; actions: string[] }> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/email-agent/run-unprocessed`, {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({ workspaceId }),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }

  async stampOutbound(threadId: string, input: {
    workspaceId: string;
    userId: string;
    gmailMessageId: string;
    internetMessageId?: string;
    fromAddress?: string;
    to?: string[];
    cc?: string[];
    subject?: string;
    snippet?: string;
    bodyText?: string;
    bodyHtml?: string;
    sentAt?: string;
  }): Promise<{ messageId: string }> {
    const hdrs = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/shared-inbox/threads/${encodeURIComponent(threadId)}/stamp-outbound`, {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({
        workspaceId: input.workspaceId,
        gmailMessageId: input.gmailMessageId,
        internetMessageId: input.internetMessageId,
        fromAddress: input.fromAddress,
        to: input.to,
        cc: input.cc,
        subject: input.subject,
        snippet: input.snippet,
        bodyText: input.bodyText,
        bodyHtml: input.bodyHtml,
        sentAt: input.sentAt,
      }),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  }
}

export const sharedInboxService = new SharedInboxService();
